"use client"

import { ArrowRight } from "lucide-react"
import Image from "next/image"
import Link from "next/link"

interface RelatedPost {
    id: string
    title: string
    excerpt: string
    slug: string
    coverImage: string
    category: string
    date: string
}

interface RelatedPostsProps {
    posts: RelatedPost[]
}

export function RelatedPosts({ posts }: RelatedPostsProps) {
    if (posts.length === 0) return null

    return (
        <section className="py-12 border-t border-white/5">
            <div className="flex items-center justify-between mb-8">
                <h3 className="text-2xl font-bold text-white">Read Next</h3>
                <Link href="/blog" className="text-sm text-premium-cyan hover:text-premium-violet transition-colors flex items-center group">
                    View all posts <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
                {posts.map((post) => (
                    <Link key={post.id} href={post.slug} className="group block h-full">
                        <article className="flex flex-col h-full bg-white/5 border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-white/20 hover:bg-white/10 hover:-translate-y-1">
                            <div className="relative w-full aspect-video overflow-hidden">
                                <Image
                                    src={post.coverImage}
                                    alt={post.title}
                                    fill
                                    sizes="(max-width: 768px) 100vw, 50vw"
                                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/20 group-hover:bg-transparent transition-colors" />
                                <div className="absolute top-4 left-4 bg-black/60 backdrop-blur-md px-3 py-1 rounded-md text-xs font-medium text-white border border-white/10">
                                    {post.category}
                                </div>
                            </div>

                            <div className="p-6 flex flex-col flex-1">
                                <h4 className="text-lg font-bold text-white mb-2 leading-snug group-hover:text-premium-cyan transition-colors">
                                    {post.title}
                                </h4>
                                <p className="text-slate-400 text-sm line-clamp-2 mb-4 flex-1">
                                    {post.excerpt}
                                </p>
                                <div className="mt-auto flex items-center text-xs text-slate-500 font-medium">
                                    <span>{post.date}</span>
                                    <span className="mx-2">·</span>
                                    <span className="text-white group-hover:underline decoration-premium-cyan/50 underline-offset-4">Read Article</span>
                                </div>
                            </div>
                        </article>
                    </Link>
                ))}
            </div>
        </section>
    )
}
